import { Bell } from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * NotificationBell
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.3 (top-bar slot).
 *
 * Minimal stub for now:
 *   - Ghost icon-only button (matching the canonical top-bar shape).
 *   - Polls `/api/notifications?unread=true&limit=1&countOnly=true` every 60s.
 *   - Renders an unread-count badge (top-right, primary fill) when > 0.
 *   - Click → navigates to `/notifications`.
 *
 * The legacy spec describes a popover dropdown with the recent activity list
 * + "Mark all as read" — that's intentionally NOT built yet. The current
 * notifications page handles the full inbox; the bell just gets you there.
 *
 * Replace this with the full popover behaviour when the notifications-page
 * inbox UX needs unifying with the top bar.
 */

interface NotificationsResponse {
  notifications: unknown[];
  total: number;
  unread?: number;
}

export function NotificationBell() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ["/api/notifications", { unread: true, limit: 1 }, sessionId],
    queryFn: async () => {
      return await api.get<NotificationsResponse>(
        "/notifications?unread=true&limit=1",
        sessionId
      );
    },
    enabled: !!sessionId,
    // Soft polling cadence — the bell is a hint, not a real-time channel.
    refetchInterval: 60_000,
    // Silently swallow auth/network errors so a flaky notifications endpoint
    // doesn't burn the top bar with toasts.
    retry: false,
  });

  // Some endpoints return `unread` separately; others use `total` when the
  // query is already filtered to unread. Try both with a sensible fallback.
  const unread = data?.unread ?? data?.total ?? 0;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setLocation("/notifications")}
      aria-label={
        unread > 0
          ? `Notifications — ${unread} unread`
          : "Notifications"
      }
      data-testid="button-notifications"
      className="relative"
    >
      <Bell className="h-5 w-5" />
      {unread > 0 ? (
        <Badge
          variant="default"
          className={cn(
            "absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 py-0",
            "text-[10px] leading-none no-default-active-elevate"
          )}
          aria-hidden
        >
          {unread > 99 ? "99+" : unread}
        </Badge>
      ) : null}
    </Button>
  );
}
